import React, { ComponentType } from 'react'

import { BRANDING_HOSTS } from '../common/models/CompanyLiveManifesto'

// Pages
import MainSkeleton from '../components/fallBack/MainSkeleton'
import EbLoadingScreen from '../components/manifesto/LoadingScreen'

const OnboardingTemplate = React.lazy(
  () => import('../components/pageTypes/onboarding/OnboardingTemplate')
)
const OnboardingJourney = React.lazy(
  () => import('../components/pageTypes/onboarding/OnboardingJourney')
)
const OnboardingSettings = React.lazy(
  () => import('../components/pageTypes/onboarding/OnboardingSettings')
)
const CompanyPage = React.lazy(() => import('../components/pageTypes/Company'))
const CompanySettings = React.lazy(
  () => import('../components/pageTypes/CompanySettings')
)
const JobMatrixPage = React.lazy(
  () => import('../components/pageTypes/JobMatrix')
)
const ProfileSettings = React.lazy(
  () => import('../components/pageTypes/ProfileSettings')
)
const ProfilePage = React.lazy(
  () => import('../components/pageTypes/ProfilePage')
)
const AddNewGoalPage = React.lazy(
  () => import('../components/pageTypes/AddNewGoal')
)
const CultureEvaluationPage = React.lazy(
  () => import('../components/pageTypes/CultureEvaluation')
)
const EvaluationPage = React.lazy(
  () => import('../components/pageTypes/Evaluation')
)
const GoalsProgress = React.lazy(
  () => import('../components/pageTypes/GoalsProgress')
)
const TermEvaluationsProgress = React.lazy(
  () => import('../components/pageTypes/TermEvaluationsProgress')
)
const FeedbackProgress = React.lazy(
  () => import('../components/pageTypes/FeedbackProgress')
)
const SignInPage = React.lazy(() => import('../components/pageTypes/SignIn'))
const TeamPage = React.lazy(() => import('../components/pageTypes/Team'))
const SwitchCompaniesPage = React.lazy(
  () => import('../components/pageTypes/SwitchCompanies')
)
const ErrorPage = React.lazy(() => import('../components/pageTypes/Error'))
const OnboardingActivitySettings = React.lazy(
  () => import('../components/pageTypes/onboarding/OnboardingActivitySettings')
)
const PeopleUpdate = React.lazy(
  () => import('../components/pageTypes/PeopleUpdate')
)
const OnboardingCreation = React.lazy(
  () => import('../components/pageTypes/onboarding/OnboardingCreation')
)
const OnboardingActivityDetail = React.lazy(
  () => import('../components/pageTypes/onboarding/OnboardingActivityDetail')
)
const EmployerBranding = React.lazy(
  () => import('../components/pageTypes/EmployerBranding')
)
const ManifestoEdit = React.lazy(
  () => import('../components/pageTypes/ManifestoEdit')
)

const FeedbackFormIntroPage = React.lazy(
  () => import('../components/pageTypes/FeedbackFormIntro')
)

const ExternalFeedbackIntroPage = React.lazy(
  () => import('../components/pageTypes/ExternalFeedbackIntro')
)

const ExternalFeedbackSubmittedPage = React.lazy(
  () => import('../components/pageTypes/ExternalFeedbackSubmitted')
)

const FeedbackOverviewPage = React.lazy(
  () => import('../components/pageTypes/FeedbackOverview')
)

const GivingFeedbackPage = React.lazy(
  () => import('../components/pageTypes/GivingFeedback')
)

const NewCompany = React.lazy(
  () => import('../components/pageTypes/NewCompany')
)

// Types
import { UserAuthToken } from './../context/AppContext'
import { CompanyModule } from '../common/enums/companyModule.enum'

export interface IRoute {
  path: string | string[]
  title: string
  component: ComponentType<any>
  exact?: boolean
  appRefreshToken?: UserAuthToken
  fallbackComponent: ComponentType<any>
  // Company entitlement axis: when set, AppRoute redirects away if the company
  // has this module disabled.
  requiredModule?: CompanyModule
  // Renders without forcing a sign-in redirect (anonymous-accessible route).
  public?: boolean
}

export interface IRoutes extends Array<IRoute> {}

// Public Employer Branding site is served on its own branded domain at the root
// (cultural-dna.nl/:slug), while the internal app stays on its primary domain. The
// same bundle serves both, so we branch on the host at runtime (BRANDING_HOSTS).
// Main app origin — target for the public site's "Log in" link. Set in env.
export const MAIN_APP_URL = process.env.REACT_APP_MAIN_APP_URL || ''

export const isBrandingHost = () =>
  window &&
  window.location.hostname &&
  BRANDING_HOSTS.some((w) => window.location.hostname.includes(w))

export const getSubdomain = () => {
  const host = window && window.location.hostname
  if (!host) return null
  const parts = host.replace(/^www\./, '').split('.')
  return parts.length > 2 ? parts[0] : null
}

// path to a manifesto tab, host-aware: branding host → [/tab] - elsewhere → /manifesto/:slug[/tab]
export const manifestoPath = (slug: string, tab?: string) =>
  `${isBrandingHost() ? '' : `/manifesto/${slug}`}${tab ? `/${tab}` : ''}`

// Only the prod app hands out branded urls; dev / demo stay on their own origin.
export const servesBrandedUrls = () => window.location.hostname === MAIN_APP_URL

// Absolute, shareable public URL: the (primary) branding host on prod, the origin
// + /manifesto prefix in dev / demo so the link stays testable and is correct.
export const publicManifestoUrl = (
  slug: string,
  isPublic: boolean,
  host = BRANDING_HOSTS[0]
) =>
  servesBrandedUrls() && isPublic
    ? `https://${slug}.${host}`
    : `${window.location.origin}/manifesto/${slug}`

export const paths = {
  // General
  signIn: '/sign-in',
  home: '/',
  pageNotFound: '*',
  switchCompanies: '/switch-companies',
  newCompany: '/switch-companies/new',
  styleDocs: '/style-docs',
  redirect: '/redirectToHome',
  company: '/company/:tab?/:sub_tab?',
  companySettings: '/company/settings/:tab?',
  // Editing one growth path's levels side by side. Takes a job id today; a
  // future comparison of unrelated standalone roles can carry them in a query.
  jobMatrix: '/company/jobs/matrix/:job_id',
  people: '/company/people',
  peopleUpdate: '/company/people/update/:id?',
  companyRituals: '/company/rituals',
  team: '/team',
  culture: '/company/culture',
  personalProfile: '/profile',
  personalProfileTab: '/profile/:tab_id?',
  peopleProfile: '/company/people/profile/:employee_id/:tab_id?',
  // Evaluation,
  progress: '/company/rituals/goals',
  termEvaluationsProgress: '/company/rituals/evaluations',
  feedbackProgress: '/company/rituals/feedback',
  cultureEvaluation: '/evaluation/:evaluationId/culture',
  feedbackFormIntro: '/feedback/form/:feedbackId',
  feedbackOverview: '/feedback/form/:feedbackId/overview',
  externalFeedbackWelcome: '/external-feedback/:externalFeedbackId',
  externalFeedbackIntro: '/external-feedback/:externalFeedbackId/intro',
  externalFeedbackOverview: '/external-feedback/:externalFeedbackId/overview',
  externalFeedbackSubmitted: '/external-feedback/:externalFeedbackId/submitted',
  editAssessment:
    '/feedback/form/:feedbackId/competency/:competencyId/assessment/:assessmentId/edit',
  evaluation: '/evaluation/:evaluationId',
  evaluationGoals: '/evaluation/:evaluationId/goals',
  evaluationSkills: '/evaluation/:evaluationId/skills',
  peopleSettings: '/company/people/profile/:employee_id/settings',
  addNewGoal: '/profile/add-new-goal',
  // Onboarding
  onboardingOverview: '/company/rituals/onboarding',
  onboardingTemplate: '/company/onboarding/template/:journey_id',
  onboardingJourney: '/company/onboarding/journey/:onboarding_id',
  onboardingActivitySettings:
    '/company/onboarding/activity-settings/:activity_id?',
  onboardingCreation: '/company/onboarding/create/:employee_id',
  onboardingActivityDetail:
    '/company/onboarding/activity/:activity_id/:template_id?',
  // Manifesto
  manifestoEdit: '/company/rituals/manifesto',
  manifesto: '/manifesto/:slug',
  careers: '/manifesto/:slug/careers',
  // NOT `culture` — that key is taken by the in-app page at /company/culture.
  manifestoCulture: '/manifesto/:slug/culture',
  // All values in one diagram, on its own chrome-less full-width page.
  manifestoCultureMatrix: '/manifesto/:slug/culture/matrix',
  library: '/manifesto/:slug/library',
}

const routes: IRoutes = [
  {
    // Single route for the public Employer Branding site; the shell handles
    // Home / Careers / Culture / Library as in-place tabs (non-exact on purpose).
    component: EmployerBranding,
    exact: false,
    path: paths.manifesto,
    public: true,
    title: 'Employer Branding',
    fallbackComponent: EbLoadingScreen,
  },
  {
    component: ManifestoEdit,
    exact: true,
    path: paths.manifestoEdit,
    requiredModule: CompanyModule.EMPLOYER_BRANDING,
    title: 'Manifesto Edit Page',
    fallbackComponent: MainSkeleton,
  },
  {
    component: SignInPage,
    exact: true,
    path: paths.signIn,
    title: 'Sign in',
    fallbackComponent: MainSkeleton,
  },
  {
    component: ProfilePage,
    exact: true,
    path: paths.home,
    title: 'Personal Profile',
    fallbackComponent: MainSkeleton,
  },
  {
    component: SwitchCompaniesPage,
    exact: true,
    path: paths.switchCompanies,
    title: 'Switch companies',
    fallbackComponent: MainSkeleton,
  },
  {
    component: OnboardingTemplate,
    exact: false,
    path: paths.onboardingTemplate,
    requiredModule: CompanyModule.ONBOARDING,
    title: 'Onboarding Template',
    fallbackComponent: MainSkeleton,
  },
  {
    component: OnboardingSettings,
    exact: false,
    path: paths.onboardingOverview,
    requiredModule: CompanyModule.ONBOARDING,
    title: 'Onboardings Overview',
    fallbackComponent: MainSkeleton,
  },
  {
    component: OnboardingJourney,
    exact: false,
    path: paths.onboardingJourney,
    requiredModule: CompanyModule.ONBOARDING,
    title: 'Onboarding Journey Overview',
    fallbackComponent: MainSkeleton,
  },
  {
    component: OnboardingActivitySettings,
    exact: false,
    path: paths.onboardingActivitySettings,
    requiredModule: CompanyModule.ONBOARDING,
    title: 'Onboarding Activity Settings',
    fallbackComponent: MainSkeleton,
  },
  {
    component: OnboardingCreation,
    exact: false,
    path: paths.onboardingCreation,
    requiredModule: CompanyModule.ONBOARDING,
    title: 'Onboarding Creation',
    fallbackComponent: MainSkeleton,
  },
  {
    component: OnboardingActivityDetail,
    exact: false,
    path: paths.onboardingActivityDetail,
    requiredModule: CompanyModule.ONBOARDING,
    title: 'Onboarding Activity Detail',
    fallbackComponent: MainSkeleton,
  },
  {
    component: GoalsProgress,
    exact: true,
    path: paths.progress,
    requiredModule: CompanyModule.PERFORMANCE_MANAGEMENT,
    title: 'Progress',
    fallbackComponent: MainSkeleton,
  },
  {
    component: FeedbackProgress,
    exact: true,
    path: paths.feedbackProgress,
    requiredModule: CompanyModule.PERFORMANCE_MANAGEMENT,
    title: 'Feedback Progress',
    fallbackComponent: MainSkeleton,
  },
  {
    component: CultureEvaluationPage,
    exact: true,
    path: paths.cultureEvaluation,
    requiredModule: CompanyModule.PERFORMANCE_MANAGEMENT,
    title: 'Culture Evaluation',
    fallbackComponent: MainSkeleton,
  },
  {
    component: FeedbackFormIntroPage,
    exact: true,
    path: paths.feedbackFormIntro,
    requiredModule: CompanyModule.PERFORMANCE_MANAGEMENT,
    title: 'Feedback Form Intro',
    fallbackComponent: MainSkeleton,
  },
  {
    component: FeedbackOverviewPage,
    exact: true,
    path: paths.feedbackOverview,
    requiredModule: CompanyModule.PERFORMANCE_MANAGEMENT,
    title: 'Feedback Overview',
    fallbackComponent: MainSkeleton,
  },
  {
    component: GivingFeedbackPage,
    exact: true,
    path: paths.editAssessment,
    requiredModule: CompanyModule.PERFORMANCE_MANAGEMENT,
    title: 'Edit assessment',
    fallbackComponent: MainSkeleton,
  },
  {
    component: ExternalFeedbackIntroPage,
    exact: true,
    path: paths.externalFeedbackWelcome,
    title: 'External Feedback Welcome',
    fallbackComponent: MainSkeleton,
  },
  {
    component: ExternalFeedbackSubmittedPage,
    exact: true,
    path: paths.externalFeedbackSubmitted,
    title: 'External Feedback Submitted',
    fallbackComponent: MainSkeleton,
  },
  {
    component: FeedbackFormIntroPage,
    exact: true,
    path: paths.externalFeedbackIntro,
    title: 'External Feedback Intro',
    fallbackComponent: MainSkeleton,
  },
  {
    component: FeedbackOverviewPage,
    exact: true,
    path: paths.externalFeedbackOverview,
    title: 'External Feedback Overview',
    fallbackComponent: MainSkeleton,
  },
  {
    component: EvaluationPage,
    exact: true,
    path: paths.evaluation,
    requiredModule: CompanyModule.PERFORMANCE_MANAGEMENT,
    title: 'Evaluation',
    fallbackComponent: MainSkeleton,
  },
  {
    component: EvaluationPage,
    exact: true,
    path: paths.evaluationGoals,
    requiredModule: CompanyModule.PERFORMANCE_MANAGEMENT,
    title: 'Evaluation – Goals',
    fallbackComponent: MainSkeleton,
  },
  {
    component: EvaluationPage,
    exact: true,
    path: paths.evaluationSkills,
    requiredModule: CompanyModule.PERFORMANCE_MANAGEMENT,
    title: 'Evaluation – Skills',
    fallbackComponent: MainSkeleton,
  },
  {
    component: TermEvaluationsProgress,
    exact: false,
    path: paths.termEvaluationsProgress,
    requiredModule: CompanyModule.PERFORMANCE_MANAGEMENT,
    title: 'Term Evaluations Progress',
    fallbackComponent: MainSkeleton,
  },
  {
    component: ProfileSettings,
    exact: false,
    path: paths.peopleSettings,
    title: 'Profile Settings',
    fallbackComponent: MainSkeleton,
  },
  {
    component: ProfilePage,
    exact: false,
    path: paths.peopleProfile,
    title: 'Employee Profile',
    fallbackComponent: MainSkeleton,
  },
  {
    component: TeamPage,
    exact: true,
    path: paths.team,
    requiredModule: CompanyModule.PERFORMANCE_MANAGEMENT,
    title: 'Team',
    fallbackComponent: MainSkeleton,
  },
  {
    component: AddNewGoalPage,
    exact: true,
    path: paths.addNewGoal,
    requiredModule: CompanyModule.PERFORMANCE_MANAGEMENT,
    title: 'Add New Goal',
    fallbackComponent: MainSkeleton,
  },
  {
    component: ProfilePage,
    exact: false,
    path: paths.personalProfileTab,
    title: 'Personal Profile',
    fallbackComponent: MainSkeleton,
  },
  {
    component: PeopleUpdate,
    exact: false,
    path: paths.peopleUpdate,
    title: 'People Update',
    fallbackComponent: MainSkeleton,
  },
  {
    component: CompanySettings,
    exact: false,
    path: paths.companySettings,
    title: 'Company Edit',
    fallbackComponent: MainSkeleton,
  },
  {
    component: JobMatrixPage,
    exact: false,
    path: paths.jobMatrix,
    title: 'Job Matrix',
    fallbackComponent: MainSkeleton,
  },
  {
    component: CompanyPage,
    exact: false,
    path: paths.company,
    title: 'Company',
    fallbackComponent: MainSkeleton,
  },
  {
    component: NewCompany,
    exact: true,
    path: paths.newCompany,
    title: 'New company',
    fallbackComponent: MainSkeleton,
  },
  {
    component: ErrorPage,
    exact: false,
    path: paths.pageNotFound,
    title: '404',
    fallbackComponent: MainSkeleton,
  },
]

// Routes registered when served on the branding host: only the public EB site,
// with the slug at the root. Non-exact so /:slug/careers, /:slug/culture and
// /:slug/library also match the shell (in-place tabs) — and so do their own
// sub-paths, like /culture/matrix. Being the sole route on this host, the greedy
// /:slug pattern has no app paths to shadow.
export const brandingRoutes: IRoutes = [
  {
    component: EmployerBranding,
    exact: false,
    path: ['/', '/careers/', '/culture', '/library'],
    public: true,
    title: 'Employer Branding',
    fallbackComponent: EbLoadingScreen,
  },
]

export default routes
